import { Button, CircularProgress, FormHelperText, Stack, Typography } from "@mui/material";
import { ControlledTextField, Form, PasswordStrengthIndicator, useHookForm } from "@powerdiary/components-library/lib";
import type { ZXCVBNResult } from "zxcvbn";

import { BaseSyntheticEvent, useCallback } from "react";
import { useClientPortalConfigContext, useResetPasswordContext } from "../../../../../context";
import { useClientPortalResetPassword } from "./useClientPortalResetPassword";
import { resetPasswordSchema, ResetPasswordType, Step, generateDefaultValues } from "./ResetPasswordDialogForm.helpers";

export default function ResetPasswordDialogForm() {
    const { clientPortalConfig } = useClientPortalConfigContext();
    const { clientPublicId } = useResetPasswordContext();

    const isSmsType = clientPortalConfig.tokenVia === "sms";

    const form = useHookForm({
        defaultValues: generateDefaultValues(clientPublicId),
        schema: resetPasswordSchema,
        mode: "onChange",
    });
    const { formState, getValues, watch, setValue, trigger } = form;
    const { resetPassword, isPending } = useClientPortalResetPassword(form);

    const password = watch("password") || "";

    const handleResetPassword = async (values: ResetPasswordType, event: BaseSyntheticEvent) => {
        await resetPassword(values, event);
    };

    const handleResult = useCallback(
        (result?: ZXCVBNResult) => {
            if (!result) return;

            const prevScore = getValues("passwordScore");

            setValue("passwordScore", result?.score || 0);
            if (prevScore !== result?.score) {
                trigger("password").catch(() => {});
            }
        },
        [getValues, setValue, trigger]
    );

    const verificationStep = getValues("step");
    const strongPassMsg = "strong password required";

    return (
        <Form form={form} onSubmit={handleResetPassword} autoComplete="off" dataTestIdSuffix="reset_password">
            <Stack gap="16px">
                <Typography variant="paragraphLargeRegular">
                    {verificationStep === Step.Initial
                        ? "Please enter a new password for your account."
                        : "Please enter the activation code received"}
                </Typography>
                {isPending ? (
                    <CircularProgress sx={{ alignSelf: "center" }} />
                ) : (
                    <>
                        {verificationStep === Step.Initial && (
                            <>
                                <ControlledTextField
                                    dataTestIdSuffix="password"
                                    type="password"
                                    label="New Password"
                                    name="password"
                                    InputProps={{
                                        autoComplete: "new-password",
                                    }}
                                />
                                <PasswordStrengthIndicator
                                    password={password}
                                    handlePasswordValidation={handleResult}
                                    customLabels={{
                                        0: `Very weak - ${strongPassMsg}`,
                                        1: `Weak - ${strongPassMsg}`,
                                    }}
                                />
                            </>
                        )}

                        {verificationStep === Step.Verification && (
                            <ControlledTextField
                                dataTestIdSuffix="verificationCode"
                                label="Verification code"
                                name="verificationCode"
                            />
                        )}

                        {formState?.errors?.root && (
                            <FormHelperText error data-test-id="form_error_root_message">
                                <>{formState.errors.root.message}</>
                            </FormHelperText>
                        )}
                    </>
                )}

                <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isPending}
                    data-test-id="button_reset_password"
                >
                    {verificationStep === Step.Initial ? "Get verification code" : "Reset password"}
                </Button>

                {verificationStep === Step.Initial && (
                    <Typography variant="body2" textAlign="center" data-test-id="text_verification_code_info">
                        {`A verification code will be sent to your ${isSmsType ? "mobile number" : "email"}.`}
                    </Typography>
                )}
            </Stack>
        </Form>
    );
}
